<template>
  <div class="starred-knowit-card">
    <div class="card-header">
      <div class="card-icon">
        <i class="el-icon-star-on" style="font-weight: bold; color: #F7BA2A;"></i>
        <span>Starred Knowit</span>
        <span class="dot">•</span>
        <span class="timestamp">{{ formattedTimestamp }}</span>
      </div>
      <div class="card-actions">
        <el-button
          type="text"
          size="mini"
          icon="el-icon-view"
          @click="viewCard"
          class="view-btn"
        >
          View the Knowit
        </el-button>
        <el-dropdown trigger="click" @command="handleCommand">
          <el-button type="text" size="mini" class="more-btn">
            <i class="el-icon-more"></i>
          </el-button>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item command="unstar">
              <i class="el-icon-star-off"></i> Unstar
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </div>
    </div>

    <div class="card-content" @click="viewCard">
      <div v-if="starredCard.card.title" class="card-title">{{ starredCard.card.title }}</div>

      <div class="card-preview">
        <div v-if="starredCard.card.content_type === 'text'" class="text-content" v-html="getContentPreview()"></div>
        <div v-else-if="starredCard.card.content_type === 'image'" class="image-content">
          <img v-if="starredCard.card.fileList && starredCard.card.fileList.length > 0"
               :src="starredCard.card.fileList[0].url"
               alt="Card image"
               style="max-width: 100%; height: auto; border-radius: 4px; max-height: 200px; object-fit: cover;">
        </div>
        <div v-else-if="starredCard.card.content_type === 'video'" class="video-content">
          <video v-if="starredCard.card.fileList && starredCard.card.fileList.length > 0"
                 controls
                 style="max-width: 100%; height: auto; border-radius: 4px; max-height: 200px;">
            <source :src="starredCard.card.fileList[0].url" :type="starredCard.card.fileList[0].mime_type">
            Your browser does not support the video tag.
          </video>
        </div>
        <div v-else-if="starredCard.card.content_type === 'quiz'" class="quiz-content">
          <p><strong>Q:</strong> {{ starredCard.card.quiz_content.question }}</p>
        </div>
      </div>

      <div v-if="starredCard.card.labels && starredCard.card.labels.length > 0" class="card-labels">
        <el-tag
          v-for="(label, index) in starredCard.card.labels"
          :key="index"
          size="mini"
          type="info"
        >
          {{ label }}
        </el-tag>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'StarredKnowitCard',
  props: {
    starredCard: {
      type: Object,
      required: true
    }
  },
  computed: {
    formattedTimestamp() {
      const date = new Date(this.starredCard.timestamp)
      const now = new Date()
      const diffInHours = Math.floor((now - date) / (1000 * 60 * 60))

      if (diffInHours < 1) {
        return 'Just now'
      } else if (diffInHours < 24) {
        return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`
      } else {
        const diffInDays = Math.floor(diffInHours / 24)
        if (diffInDays <= 10) {
          return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`
        } else {
          const month = date.toLocaleString('en-US', { month: 'short' })
          const day = date.getDate()
          const year = date.getFullYear()
          const currentYear = now.getFullYear()

          if (year === currentYear) {
            return `${month} ${day}`
          } else {
            return `${month} ${day}, ${year}`
          }
        }
      }
    }
  },
  methods: {
    getContentPreview() {
      if (!this.starredCard.card.content) return ''

      const plainText = this.starredCard.card.content.replace(/<[^>]*>/g, '')
      const maxLength = 150
      return plainText.length > maxLength ? plainText.substring(0, maxLength) + '...' : plainText
    },
    viewCard() {
      this.$emit('view', this.starredCard.card.id)
    },
    handleCommand(command) {
      if (command === 'unstar') {
        this.$emit('unstar', this.starredCard.card.id)
      }
    }
  }
}
</script>

<style scoped lang="scss">
.starred-knowit-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-left: 3px solid #F7BA2A;
  border-radius: 8px;
  padding: 16px;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: #dee2e6;
  }
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.card-icon {
  display: flex;
  align-items: center;
  gap: 8px;

  i {
    font-size: 18px;
    color: #F7BA2A;
  }

  > span {
    font-weight: 600;
    color: #F7BA2A;
    font-size: 14px;
  }

  .dot {
    color: #adb5bd;
    font-size: 14px;
    font-weight: normal;
  }

  .timestamp {
    font-size: 12px;
    color: #6c757d;
    font-weight: normal;
  }
}

.card-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.view-btn {
  color: #1159AF;
  font-size: 12px;

  &:hover {
    color: #409EFF;
  }
}

.card-content {
  cursor: pointer;

  &:hover {
    .card-title {
      color: #409EFF;
    }
  }
}

.card-title {
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 12px;
  line-height: 1.4;
  transition: color 0.2s;
}

.card-preview {
  margin-bottom: 12px;

  .text-content {
    font-size: 14px;
    color: #495057;
    line-height: 1.6;
  }

  .quiz-content {
    p {
      font-size: 14px;
      color: #495057;
      margin: 0;
      line-height: 1.5;
    }
  }
}

.card-labels {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 12px;
}
</style>
